"use client";

import Link from "next/link";
import { ArrowRight, ShieldAlert, ShieldCheck } from "lucide-react";
import TrackedGovLink from "@/components/ai-governance/TrackedGovLink";
import HeroSection from "@/components/home/HeroSection";
import EzyChatHookCard from "@/components/home/EzyChatHookCard";
import AgenticShowcaseSection from "@/components/home/AgenticShowcaseSection";
import HomeUseCasesSection from "@/components/home/HomeUseCasesSection";
import WhyAnchorSprintSection from "@/components/home/WhyAnchorSprintSection";
import ConsultationCTASection from "@/components/home/ConsultationCTASection";
import LatestInsightsSection from "@/components/home/LatestInsightsSection";
import type { BlogPostListEntry } from "@/content/blog/types";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { t } from "@/content/i18n/messages";

interface HomeProps {
  locale?: Locale;
  latestAiPosts?: readonly BlogPostListEntry[];
}

// Homepage narrative (agentic refocus): one positioning — "we build AI agents that get
// real work done" — funnelled to the use-case directory. Legacy full-stack / website /
// generic services blocks were removed from the homepage (pages remain routed).
export default function Home({
  locale = defaultLocale,
  latestAiPosts = [],
}: HomeProps) {
  return (
    <>
      <HeroSection locale={locale} />
      {/* Internal-link hook to the PHP2Shell post (keyword anchors: "PHP2Shell", "WordPress").
          Time-sensitive security news: sits first under the hero; remove or move down by end of 2026. */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            href="/blog/php2shell-php-vulnerability-wordpress-malaysia/"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="flex items-start gap-2.5 text-sm text-neutral-emphasis">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span>
                <span className="font-semibold">{t(locale, "Is your website affected by PHP2Shell?")}</span>{" "}
                <span className="text-neutral-muted">
                  {t(locale, "A critical PHP flaw can let attackers run code on WordPress servers without logging in. Here is how to check and fix it.")}
                </span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Check if your site is affected")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        </div>
      </section>
      {/* Internal-link hook to the Budget 2027 summary (lead page, gated PDF guide). Time-sensitive:
          sits right under the hero; move back down after Q1 2027. */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            href={getLocalizedPathname("/guides/malaysia-budget-2027/", locale)}
            className="group flex flex-col items-start justify-between gap-3 border-y border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">{t(locale, "Malaysia Budget 2027, made simple.")}</span>{" "}
              <span className="text-neutral-muted">
                {t(locale, "Tax, wages, business financing and the new AI measures on one page, with a free 24-page guide.")}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Read the Budget 2027 summary")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        </div>
      </section>
      {/* Internal-link hook to the AI governance hub (evergreen): free templates → AI risk platform. */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <TrackedGovLink
            href={getLocalizedPathname("/ai-governance/", locale)}
            location="home_strip"
            className="group flex flex-col items-start justify-between gap-3 border-b border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="flex items-start gap-2.5 text-sm text-neutral-emphasis">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden />
              <span>
                <span className="font-semibold">{t(locale, "Staff already using ChatGPT or Claude?")}</span>{" "}
                <span className="text-neutral-muted">
                  {t(locale, "Govern AI in 90 days with free AI governance templates for Malaysia and Singapore, from policy to AI risk register.")}
                </span>
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              {t(locale, "Get the free AI governance kit")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </TrackedGovLink>
        </div>
      </section>
      {/* Our own product, below the hero (never inside it): links out to ezychat.ai */}
      <EzyChatHookCard locale={locale} />
      <AgenticShowcaseSection locale={locale} />
      <HomeUseCasesSection locale={locale} />
      <WhyAnchorSprintSection locale={locale} />
      {/* Internal-link hook to the Claude pricing post (keyword anchor: "Claude pricing") */}
      <section className="bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            href="/guides/claude-plans-malaysia/"
            className="group flex flex-col items-start justify-between gap-3 border-y border-border py-5 sm:flex-row sm:items-center"
          >
            <span className="text-sm text-neutral-emphasis">
              <span className="font-semibold">Looking for Claude pricing in Malaysia?</span>{" "}
              <span className="text-neutral-muted">
                Compare every plan side by side and download the full report — pricing in Ringgit, capabilities, and security.
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
              Compare Claude plans
              <ArrowRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        </div>
      </section>
      <LatestInsightsSection posts={latestAiPosts} locale={locale} />
      <ConsultationCTASection locale={locale} />
    </>
  );
}
